/**
 * `ctx.graphics.create2D` — a fixed-resolution 2D canvas surface.
 *
 * The canvas2d twin of `createShaderQuad`: the host owns the canvas, the
 * letterbox, the frame loop, the pause rules, input scoping and disposal, so an
 * app is little more than its `update`/`draw`.
 *
 * Everything here exists because a standalone canvas game assumes it owns the
 * browser, and inside a windowed OS it owns nothing:
 *
 * - **Sizing** follows the parent element, not `innerWidth`/`innerHeight`.
 * - **The backing store is NOT multiplied by devicePixelRatio.** Pixel art wants
 *   one game pixel to become an N×N block; a DPR multiply resamples it and the
 *   art goes soft. This is the opposite of what `createCanvas2dSurface` does for
 *   a general-purpose surface, and it is deliberate.
 * - **Keyboard input is scoped by focus.** A page-level `keydown` listener fires
 *   while some other window is focused, and a game that calls `preventDefault`
 *   on the arrows would eat them for the whole desktop. The canvas is focusable
 *   and the listeners live on it, so the scoping is structural rather than a
 *   condition somebody has to remember to check.
 * - **The frame loop stops.** An app-owned `requestAnimationFrame` keeps running
 *   after unmount and keeps burning GPU behind a hidden tab or a minimised
 *   window. Here it is paused by document visibility and an IntersectionObserver
 *   and cancelled on dispose.
 *
 * The pure geometry lives in `surface2d-layout.mjs`, where it is tested.
 */

import {
  computeLayout,
  toLocal as toLocalPure,
  isInside,
} from "./surface2d-layout.mjs";
import type { Surface2DScaling } from "./surface2d-layout.d.mts";

export type { Surface2DScaling };

export type Surface2DOptions = {
  /** Internal resolution — the canvas backing store, never DPR-scaled. */
  width: number;
  height: number;
  /**
   * "pixel" snaps to a whole scale from 2× up (see surface2d-layout), "fit"
   * scales smoothly with letterbox bars, "stretch" fills and drops the aspect
   * ratio. Default "fit".
   */
  scaling?: Surface2DScaling;
  /** `image-rendering: pixelated`, so upscaling gives hard edges. Default false. */
  pixelated?: boolean;
  /** Painted behind the canvas, i.e. the letterbox bars. Restored on dispose. */
  background?: string;
  /** Default false — an opaque canvas is measurably cheaper to composite. */
  alpha?: boolean;
  /** CSS cursor over the surface. */
  cursor?: string;
  /**
   * `code` → name, so `keys` reads in the app's vocabulary
   * (`{ KeyW: "up", ArrowUp: "up" }` → `keys.up`). Without one, `keys` is keyed
   * by raw `KeyboardEvent.code`.
   */
  keyMap?: Record<string, string>;
  /** Codes whose default action is suppressed while focused. */
  preventDefaultKeys?: readonly string[];
  /** Take keyboard focus on mount. Default true. */
  autoFocus?: boolean;
  /** Largest dt handed to a frame callback, in seconds. Default 0.05. */
  maxDeltaSeconds?: number;
  id?: string;
};

export type Surface2DPointer = {
  /** Internal-resolution coords. Real values even outside the surface. */
  x: number;
  y: number;
  down: boolean;
  /** Bitmask, matching `PointerEvent.buttons`. */
  buttons: number;
  /** Whether x,y currently fall within the surface. */
  inside: boolean;
};

export type Surface2DInput = {
  /** Held state. Only ever true while the surface has focus. */
  keys: Record<string, boolean>;
  pointer: Surface2DPointer;
  /** Non-repeating key press. Returns an unsubscribe. */
  onKeyDown(cb: (code: string, ev: KeyboardEvent) => void): () => void;
  onKeyUp(cb: (code: string, ev: KeyboardEvent) => void): () => void;
  /** `button` matches `PointerEvent.button` (0 left, 2 right). */
  onPointerDown(cb: (button: number, p: Surface2DPointer) => void): () => void;
  onPointerUp(cb: (button: number, p: Surface2DPointer) => void): () => void;
  /** Drop every held key and the pointer — what blur does automatically. */
  clear(): void;
  /** Give the surface keyboard focus. */
  focus(): void;
  hasFocus(): boolean;
};

export type Surface2DHandle = {
  id: string;
  canvas: HTMLCanvasElement;
  ctx2d: CanvasRenderingContext2D | null;
  /** Internal resolution, as requested. */
  width: number;
  height: number;
  input: Surface2DInput;
  /** Client coords → internal coords. Letterbox- and transform-aware. */
  toLocal(ev: { clientX: number; clientY: number }): { x: number; y: number };
  /**
   * Register a frame callback. `dt` is seconds, clamped; `t` accumulates only
   * while running, so a paused surface does not fast-forward on resume.
   */
  onFrame(cb: (dt: number, t: number) => void): () => void;
  /** Explicit pause, on top of the automatic visibility rules. */
  setPaused(paused: boolean): void;
  readonly paused: boolean;
  /** True only when actually ticking — visible, on-screen and not paused. */
  readonly running: boolean;
  /**
   * Fires when `running` flips. Most of the reasons it flips are the host's
   * (tab hidden, scrolled off, window minimised), so without this an app that
   * has to react — suspending an AudioContext is the obvious one — has no
   * choice but to poll on a timer.
   */
  onRunningChange(cb: (running: boolean) => void): () => void;
  dispose(): void;
};

const DEFAULT_PREVENT = [
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "Space",
] as const;

let seq = 0;

export function createSurface2D(
  parent: HTMLElement,
  opts: Surface2DOptions
): Surface2DHandle {
  const id = opts.id || `surf2d_${++seq}_${Math.random().toString(36).slice(2, 7)}`;
  const width = Math.max(1, Math.floor(opts.width));
  const height = Math.max(1, Math.floor(opts.height));
  const scaling: Surface2DScaling = opts.scaling || "fit";
  const maxDt = opts.maxDeltaSeconds ?? 0.05;
  const keyMap = opts.keyMap;
  const preventKeys = new Set<string>(opts.preventDefaultKeys ?? DEFAULT_PREVENT);

  if (getComputedStyle(parent).position === "static") {
    parent.style.position = "relative";
  }
  const prevBackground = parent.style.background;
  if (opts.background) parent.style.background = opts.background;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.tabIndex = 0;
  canvas.style.cssText = [
    "position:absolute",
    "display:block",
    "outline:none",
    "touch-action:none",
    `cursor:${opts.cursor || "default"}`,
    opts.pixelated ? "image-rendering:pixelated" : "",
  ]
    .filter(Boolean)
    .join(";");
  parent.appendChild(canvas);

  const ctx2d = canvas.getContext("2d", { alpha: opts.alpha ?? false });
  // Upscaling a fixed-resolution surface must not interpolate, or "pixel" mode
  // produces exactly the soft art it exists to prevent. The CSS property alone
  // does not cover drawImage of the app's own offscreen canvases.
  if (ctx2d && opts.pixelated) ctx2d.imageSmoothingEnabled = false;

  // ---- layout -------------------------------------------------------------

  const relayout = () => {
    const pw = Math.max(0, parent.clientWidth);
    const ph = Math.max(0, parent.clientHeight);
    const l = computeLayout(pw, ph, width, height, scaling);
    canvas.style.width = `${l.cssW}px`;
    canvas.style.height = `${l.cssH}px`;
    canvas.style.left = `${l.offsetX}px`;
    canvas.style.top = `${l.offsetY}px`;
  };

  const ro = new ResizeObserver(relayout);
  ro.observe(parent);

  // ---- input --------------------------------------------------------------

  const keys: Record<string, boolean> = Object.create(null);
  const pointer: Surface2DPointer = {
    x: 0,
    y: 0,
    down: false,
    buttons: 0,
    inside: false,
  };

  const keyDownCbs = new Set<(code: string, ev: KeyboardEvent) => void>();
  const keyUpCbs = new Set<(code: string, ev: KeyboardEvent) => void>();
  const ptrDownCbs = new Set<(button: number, p: Surface2DPointer) => void>();
  const ptrUpCbs = new Set<(button: number, p: Surface2DPointer) => void>();

  const nameFor = (code: string) => (keyMap ? keyMap[code] : code);

  const clearInput = () => {
    for (const k of Object.keys(keys)) keys[k] = false;
    pointer.down = false;
    pointer.buttons = 0;
  };

  const onKeyDownDom = (ev: KeyboardEvent) => {
    if (preventKeys.has(ev.code)) ev.preventDefault();
    const name = nameFor(ev.code);
    if (name) keys[name] = true;
    // Auto-repeat would re-fire a discrete press; held state above covers "still down".
    if (!ev.repeat) for (const cb of [...keyDownCbs]) cb(ev.code, ev);
  };

  const onKeyUpDom = (ev: KeyboardEvent) => {
    const name = nameFor(ev.code);
    if (name) keys[name] = false;
    for (const cb of [...keyUpCbs]) cb(ev.code, ev);
  };

  const updatePointer = (ev: PointerEvent | MouseEvent) => {
    const p = toLocalPure(
      ev.clientX,
      ev.clientY,
      canvas.getBoundingClientRect(),
      width,
      height
    );
    pointer.x = p.x;
    pointer.y = p.y;
    pointer.inside = isInside(p, width, height);
  };

  const onPointerMoveDom = (ev: PointerEvent) => updatePointer(ev);

  const onPointerDownDom = (ev: PointerEvent) => {
    ev.preventDefault();
    canvas.focus();
    updatePointer(ev);
    pointer.down = true;
    pointer.buttons = ev.buttons;
    for (const cb of [...ptrDownCbs]) cb(ev.button, pointer);
  };

  // On window, not the canvas: a button released after the pointer has left the
  // surface must still clear `down`, or the app sees a stuck mouse button.
  const onPointerUpWin = (ev: PointerEvent) => {
    if (!pointer.down && ev.buttons === 0 && !pointer.inside) return;
    updatePointer(ev);
    pointer.down = false;
    pointer.buttons = ev.buttons;
    for (const cb of [...ptrUpCbs]) cb(ev.button, pointer);
  };

  const onPointerLeaveDom = () => {
    pointer.inside = false;
  };
  const onBlurDom = () => clearInput();
  const onContextMenuDom = (ev: Event) => ev.preventDefault();

  canvas.addEventListener("keydown", onKeyDownDom);
  canvas.addEventListener("keyup", onKeyUpDom);
  canvas.addEventListener("blur", onBlurDom);
  canvas.addEventListener("pointermove", onPointerMoveDom);
  canvas.addEventListener("pointerdown", onPointerDownDom);
  canvas.addEventListener("pointerleave", onPointerLeaveDom);
  canvas.addEventListener("contextmenu", onContextMenuDom);
  window.addEventListener("pointerup", onPointerUpWin);

  // ---- frame loop ---------------------------------------------------------

  const frameCbs = new Set<(dt: number, t: number) => void>();
  let rafId = 0;
  let lastTs = 0;
  let elapsed = 0;
  let explicitPaused = false;
  let onScreen = true;

  const shouldRun = () =>
    frameCbs.size > 0 && !explicitPaused && !document.hidden && onScreen;

  const tick = (ts: number) => {
    rafId = requestAnimationFrame(tick);
    // A resumed loop has a stale `lastTs`; treat the first frame as zero-length
    // rather than letting a multi-second dt teleport the simulation.
    const dt = lastTs ? Math.min(maxDt, Math.max(0, (ts - lastTs) / 1000)) : 0;
    lastTs = ts;
    elapsed += dt;
    for (const cb of [...frameCbs]) cb(dt, elapsed);
  };

  const runningCbs = new Set<(running: boolean) => void>();

  const sync = () => {
    const want = shouldRun();
    const was = rafId !== 0;
    if (want && !rafId) {
      lastTs = 0;
      rafId = requestAnimationFrame(tick);
    } else if (!want && rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
      // Anything held is a lie by the time we come back.
      clearInput();
    }
    const now = rafId !== 0;
    if (now !== was) for (const cb of [...runningCbs]) cb(now);
  };

  const io = new IntersectionObserver(
    (entries) => {
      onScreen = entries.some((e) => e.isIntersecting);
      sync();
    },
    { threshold: 0 }
  );
  io.observe(canvas);

  const onVisibility = () => sync();
  document.addEventListener("visibilitychange", onVisibility);

  // ---- teardown -----------------------------------------------------------

  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    frameCbs.clear();
    runningCbs.clear();
    keyDownCbs.clear();
    keyUpCbs.clear();
    ptrDownCbs.clear();
    ptrUpCbs.clear();
    ro.disconnect();
    io.disconnect();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pointerup", onPointerUpWin);
    canvas.removeEventListener("keydown", onKeyDownDom);
    canvas.removeEventListener("keyup", onKeyUpDom);
    canvas.removeEventListener("blur", onBlurDom);
    canvas.removeEventListener("pointermove", onPointerMoveDom);
    canvas.removeEventListener("pointerdown", onPointerDownDom);
    canvas.removeEventListener("pointerleave", onPointerLeaveDom);
    canvas.removeEventListener("contextmenu", onContextMenuDom);
    canvas.remove();
    parent.style.background = prevBackground;
  };

  const sub = <T>(set: Set<T>, cb: T) => {
    set.add(cb);
    return () => {
      set.delete(cb);
      sync();
    };
  };

  const handle: Surface2DHandle = {
    id,
    canvas,
    ctx2d,
    width,
    height,
    input: {
      keys,
      pointer,
      onKeyDown: (cb) => sub(keyDownCbs, cb),
      onKeyUp: (cb) => sub(keyUpCbs, cb),
      onPointerDown: (cb) => sub(ptrDownCbs, cb),
      onPointerUp: (cb) => sub(ptrUpCbs, cb),
      clear: clearInput,
      focus: () => canvas.focus(),
      hasFocus: () => document.activeElement === canvas,
    },
    toLocal: (ev) =>
      toLocalPure(ev.clientX, ev.clientY, canvas.getBoundingClientRect(), width, height),
    onFrame: (cb) => {
      const un = sub(frameCbs, cb);
      sync();
      return un;
    },
    setPaused: (paused) => {
      explicitPaused = paused;
      sync();
    },
    get paused() {
      return explicitPaused;
    },
    get running() {
      return rafId !== 0;
    },
    // Not routed through `sub`: a running-listener must not re-enter sync(),
    // which is what fires these callbacks in the first place.
    onRunningChange: (cb) => {
      runningCbs.add(cb);
      return () => runningCbs.delete(cb);
    },
    dispose,
  };

  relayout();
  if (opts.autoFocus !== false) {
    // After layout, and only if this document is the one the user is looking at —
    // a window restored into the background must not steal the caret.
    requestAnimationFrame(() => {
      if (!disposed && document.hasFocus() && parent.isConnected) canvas.focus();
    });
  }

  return handle;
}
